import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import _ from 'lodash';
import { Box, Button } from '@material-ui/core';
import NgpForm from '../../form/NgpForm';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFind } from '../../../services/FormQueryService';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { AdminPages, buildPageId } from '../AdminLinkUtils';
import FormSection from '../../form/FormSection';
import { useDataService } from '../../../services/ApiDataService';
import SyslogSlider from './SyslogSlider';
import { DEVICE_SYSLOG_SEVERITY, SERVER_SYSLOG_SEVERITY, SEVERITY_TO_STRING_MAPPING } from './SyslogConstants';

const functionName = AdminPages.Syslog;
const apiUrl = '/ipm/v1/admin/syslogconfig';
const defaultValues = {
    [SERVER_SYSLOG_SEVERITY]: 2,
    [DEVICE_SYSLOG_SEVERITY]: 2,
};
const serverSeverityLabel = t`Server Severity Collection`;
const deviceSeverityLabel = t`Device Severity Collection`;

function prepareSavedData(dataToSave, formData) {
    return _.extend({}, dataToSave, { _id: formData._id });
}

export default function SyslogDetail() {
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { errorWrapper } = useErrorWrapper();
    const methods = useForm(
        _.extend(USE_FORM_DEFAULT_OPTIONS, {
            defaultValues,
        })
    );
    const { reset, setError, handleSubmit } = methods;
    const breadcrumbs = [{ text: i18n._(t`Syslog Collection`) }];
    const dataService = useDataService();
    const formSaveService = useFormSaveService();

    const postFindHandler = (response) => {
        return response[0] || _.first(response) || response;
    };

    const { initialized, finding, formData, resetForm } = useFind({
        url: apiUrl,
        postFindHandler,
        resetFunc: reset,
        defaultValues,
        errorHandler: errorWrapper({}),
    });

    const handleFormSubmit = (dataToSave = {}) => {
        const putData = prepareSavedData(dataToSave, formData);

        return formSaveService.save({
            url: apiUrl,
            saveFunc: () =>
                dataService.put(`${apiUrl}/${putData._id}`, putData).then(() => {
                    resetForm();
                }),
            dataToSave: putData,
            setError,
        });
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit);

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm submitFunction={handleSubmitFunction} initialized={initialized} loading={finding || saving}>
                    <FormSection title={<Trans>Severity Setting</Trans>}>
                        <Box mt={3}>
                            <SyslogSlider
                                fieldName={SERVER_SYSLOG_SEVERITY}
                                label={serverSeverityLabel}
                                sliderParams={{ min: 0, max: 7 }}
                                valueToStringMapping={SEVERITY_TO_STRING_MAPPING}
                                initialValue={formData[SERVER_SYSLOG_SEVERITY]}
                            />
                        </Box>

                        <Box mt={3}>
                            <SyslogSlider
                                fieldName={DEVICE_SYSLOG_SEVERITY}
                                label={deviceSeverityLabel}
                                sliderParams={{ min: 0, max: 7 }}
                                valueToStringMapping={SEVERITY_TO_STRING_MAPPING}
                                initialValue={formData[DEVICE_SYSLOG_SEVERITY]}
                            />
                        </Box>
                    </FormSection>
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
